// src/services/interceptors.ts
import axios from 'axios';
import type {
    AxiosInstance,
    InternalAxiosRequestConfig, // 🔄 修改：使用 InternalAxiosRequestConfig
    AxiosResponse,
    AxiosError
} from 'axios';
import { message } from 'ant-design-vue';

// 判断是否是开发环境
const isDevelopment = import.meta.env.DEV;

// 创建axios实例
const axiosInstance: AxiosInstance = axios.create({
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: false,
});

// 请求拦截器
axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => { // 🔄 修改：参数类型改为 InternalAxiosRequestConfig
        // 从localStorage获取token
        const token = window.localStorage.getItem('token');
        if (token) {
            config.headers = config.headers || {};
            config.headers.Token = `${token}`;
        }

        // 只在非浏览器环境或不需要CORS时添加自定义头部
        if (!isDevelopment) {
            config.headers = config.headers || {};
            config.headers['X-Env'] = import.meta.env.VITE_APP_ENV || 'production';
        }

        // 开发环境日志
        if (isDevelopment) {
            console.log(`[Request] ${config.method?.toUpperCase()} ${config.url}`, {
                params: config.params,
                data: config.data,
                headers: config.headers
            });
        }

        return config;
    },
    (error: AxiosError) => { // 🔄 修改：明确错误类型
        console.error('[Request Interceptor Error]', error);
        return Promise.reject(error);
    }
);

// 响应拦截器
axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
        // 开发环境日志
        if (isDevelopment) {
            console.log(`[Response ${response.status}] ${response.config.url}`, {
                data: response.data,
                headers: response.headers
            });
        }

        return response;
    },
    (error: AxiosError) => { // 🔄 修改：明确错误类型
        // 开发环境详细错误日志
        if (isDevelopment) {
            console.error('[Response Error Details]', {
                message: error.message,
                code: error.code,
                response: error.response,
                config: error.config
            });
        }

        // 网络错误处理
        if (error.code === 'ERR_NETWORK') {
            const isCorsError = error.message.includes('CORS') ||
                (error.response?.status === 0 && error.response?.data === '');

            if (isCorsError) {
                message.error('跨域请求失败，请检查后端CORS配置');
            } else {
                message.error('网络连接失败，请检查网络设置');
            }
        } else if (error.response) {
            // HTTP状态码错误
            switch (error.response.status) {
                case 401:
                    message.error('登录已过期，请重新登录');
                    window.localStorage.removeItem('token');
                    window.location.href = '/login';
                    break;
                case 403:
                    message.error('没有访问权限');
                    break;
                case 404:
                    message.error('请求的资源不存在');
                    break;
                case 500:
                    message.error('服务器内部错误');
                    break;
                default:
                    message.error(`请求失败: ${error.response.status}`);
            }
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;